import { supabase } from '@/lib/supabase';
import { creditsChanged } from '@/lib/creditsBus';
import { deviceLabel } from '@/lib/singleLead';

/**
 * Thin client for the `membership-credits` edge function.
 *
 * Nothing here is trusted by the server: the buyer is resolved from the bearer
 * token, the credits granted are derived from the amount actually charged, and
 * the grant is keyed by the payment intent id under a row lock — so a double
 * tap, a reload or a second tab can never double charge or double credit.
 */

const call = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke('membership-credits', { body });
  if (error && !data) return { error: 'network' } as Record<string, unknown>;
  return (data || { error: 'error' }) as Record<string, unknown>;
};

export interface PackIntent {
  clientSecret: string | null;
  paymentIntentId: string | null;
  amount: number;
  credits: number;
  error?: string;
  message?: string;
}

export const createPackIntent = async (packCode: string, language: string): Promise<PackIntent> => {
  const d = await call({ action: 'pack-intent', packCode, language, device: deviceLabel() });
  return {
    clientSecret: (d.clientSecret as string) || null,
    paymentIntentId: (d.paymentIntentId as string) || null,
    amount: Number(d.amount || 0),
    credits: Number(d.credits || 0),
    error: (d.error as string) || undefined,
    message: (d.message as string) || undefined,
  };
};

export interface PackSettlement {
  ok: boolean;
  credited: boolean;
  creditsGranted: number;
  balance: number;
  extraCredits: number;
  planCredits: number;
  reason: string;
  error?: string;
}

/** Safe to call repeatedly — the grant is idempotent on the payment intent. */
export const settlePack = async (paymentIntentId: string, language = 'en'): Promise<PackSettlement> => {
  const d = await call({ action: 'pack-settle', paymentIntentId, language });
  const out: PackSettlement = {
    ok: d.ok === true,
    credited: d.credited === true,
    creditsGranted: Number(d.creditsGranted || 0),
    balance: Number(d.balance || 0),
    extraCredits: Number(d.extraCredits || 0),
    planCredits: Number(d.planCredits || 0),
    reason: (d.reason as string) || '',
    error: (d.error as string) || undefined,
  };
  if (out.ok) creditsChanged('pack-purchase');
  return out;
};

export interface CancelResult {
  ok: boolean;
  cancelAtPeriodEnd: boolean;
  periodEnd: string | null;
  remaining: number;
  error?: string;
}

/**
 * Cancel at period end. The membership stays active and every remaining credit
 * stays spendable until current_period_end — nothing is clawed back.
 */
export const setMembershipCancel = async (cancel: boolean): Promise<CancelResult> => {
  const d = await call({ action: cancel ? 'cancel' : 'resume' });
  const out: CancelResult = {
    ok: d.ok === true,
    cancelAtPeriodEnd: d.cancelAtPeriodEnd === true,
    periodEnd: (d.periodEnd as string) || null,
    remaining: Number(d.remaining || 0),
    error: (d.error as string) || undefined,
  };
  if (out.ok) creditsChanged('membership-cancel');
  return out;
};
